import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function GrantHarroldRoyalButlerMemoirPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'グラント・ハロルド王室バトラー回顧録出版' }
  ];

  const article = {
    category: '文化的権威・メディア',
    date: '2025年8月28日',
    title: 'グラント・ハロルド王室バトラー回顧録出版',
    subtitle: 'チャールズ国王の元バトラーが語る王室での7年間',
    heroImage: '/images/generated/jba-fs-081-news_grant-harrold-royal-butler-memoir.webp',
    lead: 'チャールズ国王（当時皇太子）の元バトラーであるグラント・ハロルドが、王室での7年間の経験を綴った回顧録「The Royal Butler: My Remarkable Life in Royal Service」を出版しました。労働者階級から王室バトラーへの道のりと、執事という職業の最高峰での体験が世界的注目を集めています。',
    sections: [
      {
        heading: '書籍概要と著者背景',
        image: '/images/generated/jba-fs-082-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: '王室での日々を綴った回顧録書籍のイメージ',
        paragraphs: [
          '「The Royal Butler: My Remarkable Life in Royal Service」（王室執事：王室奉仕での私の驚くべき人生）は、2025年8月28日にOrion Publishing（英国版）およびPegasus Books（米国版）から出版されました。',
          '著者のグラント・ハロルド（1978年4月26日生まれ）は、スコットランド・エアドリーの労働者階級家庭出身で、幼少期から英国王室に魅了され、王室で働くことを夢見て育ちました。現在は英国の「エチケットの第一人者」として広く知られています。'
        ]
      },
      {
        heading: '王室での経歴と職務内容',
        image: '/images/generated/jba-fs-083-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: 'コッツウォルズにある王室の邸宅ハイグローブハウスのイメージ',
        paragraphs: [
          'ハロルドは2004年から2011年まで、チャールズ皇太子（現国王チャールズ3世）のバトラーとして7年間勤務しました。主な勤務地はコッツウォルズにある王室の邸宅ハイグローブハウスで、国王チャールズ3世とカミラ王妃、ウィリアム皇太子とキャサリン妃、ヘンリー王子にバトラーとして仕えました。',
          '特別な機会には、エリザベス2世女王とフィリップ殿下にもバトラーとして奉仕する栄誉を得ており、王室バトラーとしての最高レベルの経験を積みました。'
        ]
      },
      {
        heading: '回顧録の内容と独特な視点',
        image: '/images/generated/jba-fs-084-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: '王室での日常とスタッフの交流を描く回顧録の視点のイメージ',
        paragraphs: [
          '本書は、王室とそのスタッフの間の独特な関係について、これまで語られることのなかった物語を明らかにしています。労働者階級の少年がいかにして王室の中心部に到達したか、ハイグローブハウスでの日常生活、王室メンバーとの交流、同僚スタッフとの友情とチームワークなどが詳細に描かれています。',
          'また、セレブリティや世界のリーダーとの近距離での出会い、ファーストクラスの旅行や上質な食事などの特典についても率直に語られており、王室バトラーという職業の実態を包み隠さず紹介しています。'
        ]
      },
      {
        heading: '社会的インパクトと現在の活動',
        image: '/images/generated/jba-fs-085-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: '大きな影響力を持つソーシャルメディアでの発信のイメージ',
        paragraphs: [
          '本書出版後、グラント・ハロルドはソーシャルメディアで約100万人のフォロワーを持ち、Instagram、TikTok、YouTube、Facebookで月間300万から900万のインタラクションを記録しています。',
          '現在は「The Royal Butler」というブランドで活動し、エチケットスクールを運営しています。王室での経験を活かした教育事業を通じて、執事サービスの価値と重要性を広く社会に伝える活動を続けています。'
        ]
      },
      {
        heading: '批評家の評価と注目のトピック',
        image: '/images/generated/jba-fs-086-news_grant-harrold-royal-butler-memoir-v4.webp',
        alt: '批評家からの評価と書評のイメージ',
        paragraphs: [
          'Kirkus Reviewsは本書について「王室使用人の楽しい回顧録」と評価し、「貴族家庭でのサービスは、技術と同じくらい多くの雑務を要求するが、彼は最後まで目を輝かせたままであり、同僚労働者との友情や親睦、時折の悪ふざけ、そして特典を語る」とコメントしています。',
          '注目すべきは、ハロルドが本書の中で、ヘンリー王子の回顧録「Spare」での王室描写と自身の記憶が大きく異なることを述べている点です。ハロルドは「幸せな家族」として王室を記憶しているのに対し、ヘンリー王子は苦悩を描いており、「まるで2つの異なる家庭にいたかのようだ」とコメントしています。'
        ]
      }
    ],
    comment: 'グラント・ハロルド氏の回顧録出版は、執事という職業の最高峰である王室バトラーの経験が世界的な注目を集める文化的意義を持つ画期的な出来事として、弊協会としても深く敬意を表しております。労働者階級から王室バトラーへの道のりが示すプロフェッショナリズムの重要性、そして執事という職業への社会的認知と尊敬の向上に大きく貢献するものと考えております。英国式執事サービスの伝統が持つ普遍的価値が、このような形で世界に発信されることは、執事文化の発展にとって極めて重要です。また、ソーシャルメディア時代における執事職業の新しい認知拡大の可能性を示すものとして、日本における執事サービス認知度向上への示唆に富んでおり、私どもとしても、このような文化的影響力を持つ執事の育成と社会的地位の向上に向けて、より一層努力してまいります。',
    source: [
      { label: '出典元', value: 'Amazon, Simon & Schuster, Wikipedia', link: false },
      { label: '記事URL', value: 'https://www.simonandschuster.com/books/The-Royal-Butler/Grant-Harrold/9798897100156', link: true },
      { label: 'アクセス日', value: '2025年10月', link: false }
    ],
    related: [
      { to: '/news/downton-abbey-grand-finale', image: '/images/generated/jba-fs-087-news_grant-harrold-royal-butler-memoir-v4.webp', aspect: 'aspect-[3/4]', alt: 'Downton Abbey: The Grand Finale 映画公開の関連記事', title: 'Downton Abbey: The Grand Finale 映画公開', desc: '伝説的バトラーMr. Carsonの復帰' },
      { to: '/news/raffles-butler-campaign', image: '/images/generated/jba-fs-088-news_grant-harrold-royal-butler-memoir-v4.webp', aspect: 'aspect-[3/4]', alt: 'Raffles「The Butler Did It」グローバルキャンペーンの関連記事', title: 'Raffles「The Butler Did It」グローバルキャンペーン', desc: 'ラグジュアリーホテルの執事サービス革新' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: '出典元', value: 'Amazon, Simon & Schuster, Wikipedia', link: false },
    { label: '書籍公式', value: 'https://www.simonandschuster.com/books/The-Royal-Butler/Grant-Harrold/9798897100156', link: true },
    { label: '著者略歴', value: 'https://en.wikipedia.org/wiki/Grant_Harrold', link: true },
    { label: 'インタビュー', value: 'https://www.marieclaire.com/celebrity/royals/royal-butler-grant-harrold-interview/', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];
  const toc = [
    { id: 's-overview', label: '書籍概要と著者背景' },
    { id: 's-bookinfo', label: '書籍の正式情報' },
    { id: 's-career', label: '王室での経歴と職務内容' },
    { id: 's-content', label: '回顧録の内容と独特な視点' },
    { id: 's-impact', label: '社会的インパクトと現在の活動' },
    { id: 's-anecdotes', label: '報道された王室の逸話と現在の活動' },
    { id: 's-reviews', label: '批評家の評価と注目のトピック' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Culture" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'チャールズ国王（当時皇太子）の元バトラー、グラント・ハロルドが王室での7年間を綴った回顧録を出版した',
                '2004〜2011年にハイグローブハウスで勤務し、女王やウィリアム皇太子夫妻にも仕えた最高峰の経験を持つ',
                '現在は「The Royal Butler」としてエチケット教育を展開し、SNSで約100万人のフォロワーを持つ'
              ]}
            />

            <SectionHeading id="s-overview" en="Overview">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-bookinfo" en="Book Details">書籍の正式情報</SectionHeading>
            <div className="space-y-6">
              <p className={P}>米国版はPegasus Books（Simon & Schuster配給）から刊行され、発売は<Stat>2025年9月23日</Stat>。判型はハードカバーで<Stat>272ページ</Stat>、ISBNは9798897100156、リスト価格は29.95ドルです（英国版はOrion Publishing、発売日は2025年8月28日との記載もあります）。</p>
              <p className={P}>幼少期からの王室への憧れから、ハイグローブハウスでのバトラー生活までを描いた回顧録で、王族とスタッフの関係やエチケットを「未公開の話」として綴っています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/harrold-simonschuster.jpg"
              alt="回顧録The Royal Butlerの書誌情報を掲載するSimon & Schuster公式サイトのスクリーンショット"
              media="Simon & Schuster（公式）"
              url="https://www.simonandschuster.com/books/The-Royal-Butler/Grant-Harrold/9798897100156"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="まるで2つの異なる家庭にいたかのようだ。" cite="Grant Harrold" />
            </div>

            <SectionHeading id="s-career" en="Royal Career">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-content" en="The Memoir">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-impact" en="Social Impact">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-anecdotes" en="Royal Anecdotes">報道された王室の逸話と現在の活動</SectionHeading>
            <div className="space-y-6">
              <p className={P}>報道された逸話には、2005年にバルモラル城のギリーズ・ボールでエリザベス2世女王とダンスをしたこと、ハリー王子に水風船を投げつけられたことなどがあります。スタッフ同士の親睦や、王室の非公式な一面が率直に描かれています。</p>
              <p className={P}>ハロルドは2011年5月にハイグローブハウスを退職し（役職の冗長化による）、現在は「The Royal Butler」としてエチケットの専門家・メディアコメンテーター・講演者として活動。Cunard、Princess Cruises、Seabournなどのクルーズ船でも講演を行っています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/harrold-wikipedia.jpg"
              alt="グラント・ハロルドの経歴を解説するWikipedia英語版のページのスクリーンショット"
              media="Wikipedia（Grant Harrold）"
              url="https://en.wikipedia.org/wiki/Grant_Harrold"
            />

            <SectionHeading id="s-reviews" en="Reception">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">出版社</dt><dd className="text-[#333] mt-0.5">Pegasus Books</dd></div>
                  <div><dt className="text-[#8f7a56]">発売</dt><dd className="text-[#333] mt-0.5">2025年9月23日（米）</dd></div>
                  <div><dt className="text-[#8f7a56]">判型</dt><dd className="text-[#333] mt-0.5">ハードカバー272頁</dd></div>
                  <div><dt className="text-[#8f7a56]">価格</dt><dd className="text-[#333] mt-0.5">29.95ドル</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
